import { MouseEventHandler } from 'react';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import useAppContext from 'flow/hooks/useAppContext';
import { useModal } from 'flow/contexts/ModalProvider';
import { handleCtiClick } from 'flow/actions/cti-actions';
import { getSelectedCtiContactByNumber } from 'flow/selectors/cti-integration-selectors';
import { selectUserCountryCode } from 'flow/selectors/ExtensionSelectors';
import { selectCallRecordFromKey } from 'flow/selectors/call-selectors';
import Icon from 'flow/components/ui-components/Icon';
import IconButton from 'flow/components/ui-components/IconButton';
import { CallRecordKey } from 'flow/types/EntityKeys';
import { getContactFromCallRecord } from 'flow/utils/call-record-utils';

interface Props {
    callRecordKey?: CallRecordKey;
}

const CallRecordContactButton = ({ callRecordKey }: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const { openModal } = useModal();

    const callRecord = useAppSelector((state) =>
        callRecordKey
            ? selectCallRecordFromKey(state, callRecordKey)
            : undefined,
    );
    const callerNumber = callRecord?.getIn(['number', 'number']);

    const integrationContact = useAppSelector((state) =>
        getSelectedCtiContactByNumber(state, callerNumber),
    );

    const ownCountryCode = useAppSelector(selectUserCountryCode);

    if (!callRecord) return null;

    const contact = callRecord.get('contact');
    const hasContact = !!(contact || integrationContact);

    const onClick: MouseEventHandler = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (integrationContact) {
            dispatch(
                handleCtiClick(integrationContact.get('contactRoute'), null, {
                    number: integrationContact.get('number'),
                }),
            );
        } else if (contact) {
            openModal('callRecordContactModal', { callRecordKey });
        } else {
            openModal('createContact', {
                contact: getContactFromCallRecord(callRecord),
                countryCode: ownCountryCode?.toUpperCase(),
            });
        }
    };

    return (
        <IconButton
            onClick={onClick}
            tooltipLabel={i18next.t(
                hasContact
                    ? 'Chat.Details.GoToContactCard'
                    : 'Colleagues.Create.External.Title',
            )}
            className="size-10 rounded-sm bg-gray-200"
        >
            <Icon iconKey={hasContact ? 'account' : 'person_add'} />
        </IconButton>
    );
};

export default CallRecordContactButton;
